/**
 * AppConfigTab - Admin App Configuration Tab
 *
 * Allows admin to configure application settings like free account operation limits.
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../services/api';

interface SystemConfig {
  maintenanceMode: boolean;
  registrationEnabled: boolean;
  maxFreeAccounts: number;
  freeOperationLimit: number;
  subscriptionPrices: {
    monthly: number;
    annual: number;
  };
  databaseType: 'sqlite' | 'postgres';
}

export default function AppConfigTab() {
  const { t } = useTranslation();
  const [config, setConfig] = useState<SystemConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [freeOperationLimit, setFreeOperationLimit] = useState<number>(50);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get<{ success: boolean; data: SystemConfig }>('/admin/config');
      if (response.data.success && response.data.data) {
        setConfig(response.data.data);
        setFreeOperationLimit(response.data.data.freeOperationLimit);
      }
    } catch (err) {
      setError(t('admin.config.loadError', 'Erreur lors du chargement de la configuration'));
      console.error('Error loading config:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      
      const response = await api.put<{ success: boolean; data: SystemConfig }>('/admin/config', {
        freeOperationLimit,
      });
      
      if (response.data.success && response.data.data) {
        setConfig(response.data.data);
        setSuccess(t('admin.config.saveSuccess', 'Configuration enregistrée avec succès'));
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (err) {
      setError(t('admin.config.saveError', 'Erreur lors de l\'enregistrement de la configuration'));
      console.error('Error saving config:', err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Error message */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Success message */}
      {success && (
        <div className="bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-400 px-4 py-3 rounded-lg">
          {success}
        </div>
      )}

      {/* System Information Section */}
      {config && (
        <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-6">
          <h3 className="text-lg font-medium text-slate-900 dark:text-slate-100 mb-4">
            {t('admin.config.systemInfo.title', 'Informations système')}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="flex items-center space-x-3">
              <div className={`w-3 h-3 rounded-full ${config.databaseType === 'postgres' ? 'bg-blue-500' : 'bg-amber-500'}`}></div>
              <div>
                <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
                  {t('admin.config.systemInfo.database', 'Base de données')}
                </p>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {config.databaseType === 'postgres' ? 'PostgreSQL' : 'SQLite'}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Free Account Limits Section */}
      <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-6">
        <h3 className="text-lg font-medium text-slate-900 dark:text-slate-100 mb-4">
          {t('admin.config.freeAccountLimits.title', 'Limites des comptes gratuits')}
        </h3>
        <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">
          {t('admin.config.freeAccountLimits.description', 'Configurez les limitations pour les utilisateurs avec un compte gratuit.')}
        </p>

        <div className="space-y-4">
          {/* Monthly Operation Limit */}
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <label 
                htmlFor="freeOperationLimit" 
                className="block text-sm font-medium text-slate-700 dark:text-slate-300"
              >
                {t('admin.config.freeAccountLimits.operationLimit', 'Limite d\'opérations')}
              </label>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                {t('admin.config.freeAccountLimits.operationLimitDescription', 'Nombre maximum d\'opérations qu\'un compte gratuit peut avoir.')}
              </p>
            </div>
            <div className="ml-4">
              <input
                type="number"
                id="freeOperationLimit"
                min="1"
                max="10000"
                value={freeOperationLimit}
                onChange={(e) => setFreeOperationLimit(parseInt(e.target.value, 10) || 1)}
                className="w-24 px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-right focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
              />
            </div>
          </div>

          {/* Current value display */}
          {config && config.freeOperationLimit !== freeOperationLimit && (
            <p className="text-sm text-amber-600 dark:text-amber-400">
              {t('admin.config.freeAccountLimits.currentValue', 'Valeur actuelle : {{value}}', { value: config.freeOperationLimit })}
            </p>
          )}
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving || !config || config.freeOperationLimit === freeOperationLimit}
          className="px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {saving ? t('common.saving', 'Enregistrement...') : t('common.save', 'Enregistrer')}
        </button>
      </div>
    </div>
  );
}
